import { PERMISSION, hasPermission } from '@/models/permission';
import { isAdmin, isAdminTenant, isManager } from '@/models/role';
import { AuthService } from '@/services/auth.service';
import { StorageService } from '@/services/storage.service';
import { UserService } from '../services/user.service';

const initialState = () => ({
  token: StorageService.getToken(),
  currentUser: StorageService.getCurrentUser(),
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  login({ commit }, { email, password, rememberMe }) {
    commit('LOGIN');

    return AuthService.login(email, password, rememberMe)
      .then(({ user, token }) => {
        commit('SUCCESS', { user, token });

        return { user, token };
      })
      .catch((error) => {
        commit('ERROR', error.response.data);

        // Throw the error, it's handled in the view
        throw error.response.data;
      });
  },
  validate({ state, commit, dispatch, rootState }) {
    return AuthService.validate()
      .then((user) => {
        // Apply the tenant's terminology overlay once per session (idempotent; guarded in the module).
        dispatch('terminology/load', null, { root: true });
        const currentTenant = rootState.tenant.tenant;
        const tenantHasChanged =
          !currentTenant || !state.currentUser || state.currentUser.tenantId !== currentTenant.id;
        const userHasChanged = !state.currentUser || state.currentUser.id !== user.id;
        // Always adopt the freshly fetched user: fields that change without the identity changing
        // (permissions, role) would otherwise stay stale until a full page reload.
        commit('SET_CURRENT_USER', user);
        // avoid re-fetching the tenant if it didn't change
        if (!userHasChanged && !tenantHasChanged) {
          return;
        }
        dispatch('tenant/getTenant', user.tenantId, { root: true });
        dispatch('tenantLocations/getTenantLocations', null, { root: true });
      })
      .catch((error) => {
        dispatch('logout');

        throw error;
      });
  },
  resetPassword({ commit }, email) {
    return AuthService.resetPassword(email);
  },
  checkTokenValidity({ commit }, token) {
    return AuthService.checkTokenValidity(token);
  },
  changePassword({ commit }, { password, token }) {
    return AuthService.changePassword(password, token);
  },
  editUser({ state, commit }, user) {
    const id = state.currentUser.id;

    return UserService.patchUser(id, user).then((user) => commit('SET_CURRENT_USER', user));
  },
  saveLocale({ state, commit }, locale) {
    const id = state.currentUser.id;

    return UserService.patchUser(id, { locale }).then((user) => commit('SET_CURRENT_USER', user));
  },
  logout({ commit, dispatch }) {
    commit('LOGOUT');
    // Revert terminology to defaults so the next tenant on this page load starts clean.
    dispatch('terminology/reset', null, { root: true });
    // Same for billing: the banners' state, the admin lists and `mine` are the previous tenant's,
    // and must not survive into the next login on this page load — not even for the moment before
    // the fresh fetch answers.
    commit('billing/RESET', null, { root: true });

    return AuthService.logout();
  },
};

const mutations = {
  SET_CURRENT_USER(state, user) {
    state.currentUser = user;
  },
  LOGIN(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SUCCESS(state, { user, token }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.currentUser = user;
    state.token = token;
  },
  LOGOUT(state) {
    state.status = {
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.token = undefined;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
};

const getters = {
  authenticated: (state) => !!state.token,
  currentUser: (state) => state.currentUser,
  isAdmin: (state) => isAdmin(state.currentUser && state.currentUser.role),
  isAdminTenant: (state) => isAdminTenant(state.currentUser && state.currentUser.role),
  isManager: (state) => isManager(state.currentUser && state.currentUser.role),

  canDecideGoNoGo: (state) => hasPermission(state.currentUser, PERMISSION.GO_NO_GO_DECIDE),

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const auth = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
